// AN ACTION ITEM'S WORDS — its statuses, its fields, and the audit kind each
// field's history is kept under.
//
// ⭐ PURE, so the page, the model's read (lib/server/client-slack-items.ts) and
// the store (lib/server/action-items.ts) share one list. The History tab reads
// audit_event by these kinds; the store writes them.
//
// Erasable TypeScript: the sidecar's tick imports this under node's type stripping.

/** An item's four statuses, in the page's order. Who owes is its own field, and a standing rule is a type. */
export const ACTION_ITEM_STATUSES = ["not_started", "in_progress", "done", "dropped"] as const;
export type ActionItemStatus = (typeof ACTION_ITEM_STATUSES)[number];

/**
 * Each status in a person's words, wherever a person sees one: the page, the
 * editor, the History tab, the Client Inbox, the Weekly Report's editing view.
 * `not_started` reads "To do" (Jira's word) and stays `not_started` in the
 * database.
 */
export const STATUS_WORDS: Record<ActionItemStatus, string> = {
  not_started: "To do",
  in_progress: "In progress",
  done: "Done",
  dropped: "Dropped",
};

export const isActionItemStatus = (v: unknown): v is ActionItemStatus => (ACTION_ITEM_STATUSES as readonly unknown[]).includes(v);


/** Done and dropped: closed, under Done (or off the Weekly Report), and not something to suggest work on. */
export const isClosedStatus = (s: ActionItemStatus | null): boolean => s === "done" || s === "dropped";

export type Owes = "us" | "them";

/**
 * The fields a person sets (the editor's form), and the audit kind each change
 * to it is kept under (one kind per field). ⚠️ THE MODEL WRITES NONE OF THESE
 * after it opens an item BUT `owes`; it opens one with title, description,
 * owes, assignee and escalation, and from then on writes only its suggestion
 * (`SUGGESTION_KIND`), `escalation`, the messages it links (`MESSAGES_KIND`),
 * and, since items v4, `owes`: who has the ball, moved by the ledger unless a
 * person's own is newer (lib/server/action-items.ts `refreshLedgerBall`).
 *
 * `type` is the one person field that isn't a column of
 * `action_item`: it lives in `action_item_type`, which the model writes for
 * the items it reads, with no history row. A person's type has its row here,
 * and from then on the model's read never writes over it.
 *
 * `kind`, the work type, is the same shape: it lives in
 * `action_item_kind`, which the Work Type skill writes with no history row, and
 * a person's kind has its row here and is never written over after.
 */
export const PERSON_FIELDS = {
  title: "action_item.title",
  description: "action_item.description",
  status: "action_item.status",
  assigneeEmail: "action_item.assignee",
  owes: "action_item.owes",
  due: "action_item.due",
  inReport: "action_item.in_report",
  notes: "action_item.notes",
  archived: "action_item.archived",
  type: "action_item.type",
  kind: "action_item.kind",
} as const;
/**
 * The fields a person can still write. ⚠️ `inReport` ("Show this week") is
 * RETIRED (the Weekly Report picks its action items now) and its column is
 * gone. Its kind stays in `PERSON_FIELDS` so old History rows still read
 * "Weekly Report", but nothing writes one.
 */
export type PersonField = Exclude<keyof typeof PERSON_FIELDS, "inReport">;

/**
 * The brand an item is for: written once, when it is opened. Payload
 * `{ brandSlug, source }`, and `addedBy: ITEMS_CHECK_ADDED_BY` on an item
 * the Missed Items check opened: the mark an AM sees.
 */
export const BRAND_KIND = "action_item.brand";
/** Who opened an item when it wasn't the items read: the opening row's `addedBy`. */
export const ITEMS_CHECK_ADDED_BY = "missed_items_check";
/** Escalation: the model's to set, on opening and on every later read. Payload `{ escalation }`. */
export const ESCALATION_KIND = "action_item.escalation";
/**
 * The model's suggested status and its one line of why. Payload `{ status, why }`,
 * by the model; or, by a person, `update` to null: setting a status cleared it.
 */
export const SUGGESTION_KIND = "action_item.suggestion";
/** Slack messages linked to the item (`create`), by the model or a person. Payload `{ channelId, ts: [...] }`. */
export const MESSAGES_KIND = "action_item.messages";

/**
 * The re-check of a quiet item (lib/server/client-slack-recheck.ts): by the
 * model, one row per verdict that acts. Payload
 * `{ verdict, reason, quietDays, since, closeAs?, intoId?, intoTitle?,
 * fromId?, fromTitle? }`: an ending (with the suggestion row beside it), a
 * merge (on the later item, `intoId`; on the earlier, `fromId`), or a re-check
 * that kept it open. Words, never a client's sentence: the skill's reason.
 */
export const RECHECK_KIND = "action_item.recheck";
/**
 * Inactive: the model marks a quiet item inactive (`{ inactive: true,
 * quietDays, since, why }`), a person re-opens it (`{ inactive: false }`).
 */
export const INACTIVE_KIND = "action_item.inactive";

/** Every kind an item's history is made of, for the History tab's one read. */
export const ACTION_ITEM_KINDS: readonly string[] = [
  BRAND_KIND,
  ...Object.values(PERSON_FIELDS),
  ESCALATION_KIND,
  SUGGESTION_KIND,
  MESSAGES_KIND,
  RECHECK_KIND,
  INACTIVE_KIND,
];

/**
 * Who the model is in audit_event: a history row by this address is "✦ Model"
 * on the History tab, and every other address is a person. The `.invalid`
 * domain and role `system` are the house shape for a writer that is not a
 * person (`CLIENT_SLACK_SYNC_ACTOR`, `CRM_SYNC_ACTOR`).
 */
export const ACTION_ITEM_MODEL_EMAIL = "client-slack-model@ironside.invalid";
